import { Inject, Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { Distributionanalyticsv1PortfolioAnalyticsResponse, V1PortfolioAnalytics } from '@blk/portfolio-security-server/analytics/portfolio-analytics';
import { V1ListModelFamilyRequest, V1ModelType } from '@blk/portfolio-security-server/models';
import { BenchmarkPortfolio, InputPortfolio, WweInputTotalPortfolio } from '@blk/wealth-web-endpoint';
import { ModelsEntitlementService } from '@ddt-web/common';
import { APP_STORE_CONFIG_TOKEN, CommonDCR, ERROR_CODES, ERROR_SEVERITY, ErrorDCR } from '@ddt-web/config';
import { ConfigService, InjectConfig, PeConfigKey, SharedConfigKey } from '@ddt-web/config-layer';
import { AnalysisParams, ApiService, ApiSettings, ApiStoreConfiguration, AppStoreConfig, DataServiceApiConstants, PasDataServiceApiConstants, PasRcResolverService, PortfolioAnalysisOptions, WwePortfolioAnalysisServiceParams } from '@ddt-web/data-service';
import { SharedApplicationConstants } from '@ddt-web/domain';
import { ErrorObject, PasModelFamiliesError, WwePortfolioAnalysisPartialError } from '@ddt-web/error-handler';
import { PasApiParamUtil, PasApiService, PortfolioAnalysisStoreMappingConfig } from '@ddt-web/pas';
import { StoreService } from '@ddt-web/store-service';
import { WealthWebApiParamUtil } from '@ddt-web/wealth-web';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as compact from 'lodash/compact';
import * as endsWith from 'lodash/endsWith';
import * as flatten from 'lodash/flatten';
import * as get from 'lodash/get';
import * as head from 'lodash/head';
import * as isEmpty from 'lodash/isEmpty';
import * as isNil from 'lodash/isNil';
import * as keys from 'lodash/keys';
import * as last from 'lodash/last';
import * as pick from 'lodash/pick';
import * as replace from 'lodash/replace';
import * as split from 'lodash/split';
import * as toPairsIn from 'lodash/toPairsIn';
import * as uniq from 'lodash/uniq';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, first, map, switchMap, take } from 'rxjs/operators';

@Injectable()
@UntilDestroy()
export class PasDataPortfolioAnalysisService {

	// RemoveStaleAnalysisForFailedPortfolios currently only set to true for PA to handle universe discrepancies between WDS and AWA
	@InjectConfig(SharedConfigKey.RemoveStaleAnalysisForFailedPortfolios) private readonly removeStaleAnalysisForFailedPortfolios: boolean = false;
	@InjectConfig(SharedConfigKey.SHOULD_REMOVE_PREVIOUS_PERFORMANCE_ANALYSIS) private readonly shouldRemovePreviousPerformanceAnalysis: boolean = false;
	@InjectConfig(SharedConfigKey.SHOULD_GET_COMMON_PERIOD) private readonly shouldGetCommonPeriod: boolean = false;
	@InjectConfig(SharedConfigKey.ENABLE_TPM_FOR_DOT_COM) private readonly isTpmEnabledForDotCom: boolean;
	@InjectConfig(SharedConfigKey.ENABLE_ADAM_MODEL_NAMES) private readonly enableAdamModelNames: boolean;
	@InjectConfig(SharedConfigKey.ENABLE_CMS_RENAMING) private readonly enableCmsRenaming: boolean;
	@InjectConfig(SharedConfigKey.ENABLE_THIRD_PARTY_API) private readonly enableThirdPartyApi: boolean = true;
	@InjectConfig(SharedConfigKey.ENABLE_PAS_THIRD_PARTY_API) private readonly enablePasThirdPartyApi: boolean = false; // this flag will come into figure when the flag is enabled. This flag decides which third party api need to be made PAS or the existing one.
	@InjectConfig(SharedConfigKey.ENABLE_CUSTOM_MODELS_API) private readonly enableCustomModelsApi: boolean = true;
	@InjectConfig(SharedConfigKey.SHOULD_ADD_BENCHMARK_PERF_ANALYSIS_AT_ROOT) private readonly shouldAddBenchPerfAnalysisAtRoot: boolean = false;
	@InjectConfig(SharedConfigKey.SHOULD_IGNORE_EMPTY_PAS_SLEEVE) private readonly shouldIgnoreEmptyPasSleeve: boolean = false;
	@InjectConfig(SharedConfigKey.FLIP_MODELS_CONFIG_STORE_IDENTIFIER_TO_DAS_PORTFOLIO_ID) private readonly flipModelsConfigStoreIdentifierToDasPortfolioId: boolean = true;

	constructor(
		@Inject(APP_STORE_CONFIG_TOKEN) protected storeConfig: AppStoreConfig,
		protected storeService: StoreService,
		private contentService: ContentService,
		protected pasApiService: PasApiService,
		protected configService: ConfigService,
		protected pasRcResolver: PasRcResolverService,
		protected modelsEntitlementService: ModelsEntitlementService,
		protected apiService: ApiService
	) { }

	public loadPortfolioAnalysisData(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		this.dispatchAllDataLoaders(configs, generalAnalysisParams, portfolioAnalysisOptions);
	}

	/**
	 * @description loadDefaultStoreData: performs the appropriate api calls and updates the corresponding stores.
	 * @param {apiSettings} apiSettings: api specific settings: rcs, config, and portfolioAnalysisParams.
	 * @param {AnalysisParams} analysisParams: the params used to build the api call.
	 */
	public loadDefaultStoreData(apiSettings: ApiSettings, analysisParams: AnalysisParams, portfolioAnalysisOptions?: PortfolioAnalysisOptions): void {
		const useAladdinId = this.configService.get(SharedConfigKey.AWE_USE_ALADDINID, false);
		const portfolios = WealthWebApiParamUtil.createPortfolioParams(analysisParams?.analysisParamsArray, useAladdinId);
		const benchmark = WealthWebApiParamUtil.createBenchmarkParam(analysisParams, useAladdinId);

		if (this.isValidAnalysisData(portfolios)) {
			if (apiSettings?.config?.name?.includes('performance') && this.shouldGetCommonPeriod) {
				this.fetchAndLoadPerformanceWithCommonPeriod(apiSettings, analysisParams, portfolios, benchmark, portfolioAnalysisOptions);
			} else {
				this.fetchAndLoadDefault(apiSettings, analysisParams, portfolios, benchmark);
			}

		} else {
			this.storeService.upsertMany(apiSettings.config, []);
		}
	}

	public fetchPortfolioAnalyserConfig(configs: Array<any>): void {
		configs.forEach(config => this.storeService.fetch(config));
		// Retrieve models info
		// eslint-disable-next-line @nrwl/nx/workspace/killSubscriptionRule
		forkJoin([
			this.modelsEntitlementService.isEligibleForBLKManagedModels(),
			this.modelsEntitlementService.isEligibleForThirdPartyModels()
		]).pipe(
			switchMap(([isEligibleForBLKManagedModels, isEligibleForThirdPartyModels]) => {
				const handleError = catchError(error => {
					console.error('Error retrieving Models. Status code:', error.status, error);
					return of({});
				});

				const defaultModelsRequestParams: V1ListModelFamilyRequest = PasApiParamUtil.createModelFamiliesParams([V1ModelType.Blk, V1ModelType.Custom]);
				const thirdPartyRequestParams: V1ListModelFamilyRequest = PasApiParamUtil.createModelFamiliesParams([V1ModelType.ThirdParty]);

				const apisToCall = {
					defaultModels: this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.MODEL_FAMILIES).pipe(
						first(),
						map((rc: string) => ({ ...defaultModelsRequestParams, rc })),
						switchMap((requestParams) => this.pasApiService.fetchModelFamilies(requestParams).pipe(handleError))
					),
					managedModels: this.apiService.fetchManagedModelsConfig(),
					thirdParty: (this.enablePasThirdPartyApi) ? this.pasRcResolver.getRcForUserType(PasDataServiceApiConstants.MODEL_FAMILIES).pipe(
						first(),
						map((rc: string) => ({ ...thirdPartyRequestParams, rc })),
						switchMap((requestParams) => this.pasApiService.fetchModelFamilies(requestParams).pipe(handleError))
					) : this.apiService.fetchThirdPartyModelsList().pipe(handleError)
				};

				const shouldUseManagedModels = isEligibleForBLKManagedModels;
				if (!shouldUseManagedModels) {
					delete apisToCall.managedModels;
				}

				if (!this.isTpmEnabledForDotCom) {
					const shouldUseThirdPartyApi = isEligibleForThirdPartyModels && this.enableThirdPartyApi;
					if (!shouldUseThirdPartyApi) {
						delete apisToCall.thirdParty;
					}
				}

				if (!this.enableCustomModelsApi) {
					delete apisToCall.defaultModels;
				}

				return forkJoin([forkJoin(apisToCall), of(isEligibleForBLKManagedModels)]);

			})
		).subscribe(([responseJson, isEligibleForBLKManagedModels]) => {
			const response = this.getModelPortfoliosListResponse(responseJson, isEligibleForBLKManagedModels);
			// config response has no success or allDataReturned attribute, so skip error handle
			this.updateStoreFromResponse(configs, response, responseJson, true);
			const modelFamiliesResponse = (responseJson as any).defaultModels;
			if (modelFamiliesResponse?.error) {
				const errorSeverity = modelFamiliesResponse?.modelFamilies?.length ? ERROR_SEVERITY.WARNING : ERROR_SEVERITY.CRITICAL;
				throw new PasModelFamiliesError(modelFamiliesResponse.error.code, modelFamiliesResponse.error.message, errorSeverity);
			}
		}, error => {
			this.updateStoreFromResponse(configs, null);
			throw new PasModelFamiliesError(error?.error?.['error-id'], error?.error?.errorDescription, ERROR_SEVERITY.ERROR);
		});
	}

	private fetchAndLoadDefault(
		apiSettings: ApiSettings,
		analysisParams: AnalysisParams,
		portfolios: InputPortfolio[],
		benchmark: BenchmarkPortfolio
	) {
		this.pasRcResolver.getRcForUserType(apiSettings.config.name)
			.pipe(
				untilDestroyed(this),
				map((rc: string) => ({
					...apiSettings,
					wwePortfolioAnalysisParams: {
						body: {
							portfolios,
							benchmark: apiSettings.config.name === 'performanceExBenchmark' ? null : benchmark,
							simulatedPerformanceUserInputs: analysisParams?.simulatedPerformanceUserInputs
						} as WweInputTotalPortfolio,
						portfolios: analysisParams.analysisParamsArray,
						rc
					} as WwePortfolioAnalysisServiceParams
				})),
				switchMap((settings: ApiSettings) => {
					return this.dispatchFetchAwaPortfolioAnalysisApiCall(settings).pipe(map((response => [settings, response])));
				})
			)
			.subscribe(([settings, response]: [ApiSettings, Distributionanalyticsv1PortfolioAnalyticsResponse]) => {
				this.processSuccessfulAnalysis(get(response, 'portfolioAnalytics', []).filter(port => isNil(port.status)), settings);
				this.processFailedAnalysis(response, settings);
			});
	}

	private fetchAndLoadPerformanceWithCommonPeriod(
		apiSettings: ApiSettings,
		analysisParams: AnalysisParams,
		portfolios: InputPortfolio[],
		benchmark: BenchmarkPortfolio,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		// removing previous performance from store to ensure we don't show incorrect data
		if (this.shouldRemovePreviousPerformanceAnalysis) {
			const storesToUpdate = [this.storeConfig.performance, this.storeConfig.performanceCommonPeriods];
			const idsToRemove = get(analysisParams, 'analysisParamsArray', []).map(analysisParam => analysisParam.uniqueId || analysisParam.portfolioId);

			storesToUpdate.forEach(performanceStore => {
				this.storeService.removeMany(performanceStore, idsToRemove);
			});
		}

		this.pasRcResolver.getRcForUserType(apiSettings.config.name)
			.pipe(
				untilDestroyed(this),
				map((rc: string) => {
					return {
						...apiSettings,
						wwePortfolioAnalysisParams: {
							body: {
								portfolios,
								benchmark: null, // individual performance will never include benchmark as it is restricting the portfolio individual performance interval
								simulatedPerformanceUserInputs: analysisParams?.simulatedPerformanceUserInputs
							} as WweInputTotalPortfolio,
							portfolios: analysisParams.analysisParamsArray,
							rc
						} as WwePortfolioAnalysisServiceParams
					};
				}),
				switchMap((settings: ApiSettings) => {
					return this.dispatchFetchAwaPortfolioAnalysisApiCall(settings).pipe(map((response => [settings, response])));
				}),
				switchMap(([settings, response]: [ApiSettings, Distributionanalyticsv1PortfolioAnalyticsResponse]) => {
					const portfoliosPerformanceMetrics = response.portfolioAnalytics
						.map(portfolio => portfolio?.totalPortfolioAnalysis?.portfolioAnalysis?.performance?.exPostMetrics?.[0]);
					const portfolioMonths = portfoliosPerformanceMetrics.map(metrics => metrics?.months);

					// PAS will always align the end date, so we can get from first one
					const endDateAsString = portfoliosPerformanceMetrics?.[0]?.interval?.stopDate;

					// if the analysis has benchmark or portfolio have different months period, we need to get common period data (otherwise we already have it)
					const hasBenchmark = !!benchmark;
					const isStartDateMisaligned = compact(uniq(portfolioMonths)).length > 1 && endDateAsString;
					const maxMonthsOverride = Math.min(...portfolioMonths);

					if (!maxMonthsOverride) {
						// if maxMonths is 0/null/undefined we need to return {} as response for common period
						return of([response, {}]);
					}
					if (hasBenchmark || isStartDateMisaligned) {

						settings.wwePortfolioAnalysisParams.body['benchmark'] = benchmark; // common period always should have benchmark set
						settings.wwePortfolioAnalysisParams.body['simulatedPerformanceUserInputs'] = {
							...settings.wwePortfolioAnalysisParams.body['simulatedPerformanceUserInputs'],
							maxMonthsOverride
						};
						return this.dispatchFetchAwaPortfolioAnalysisApiCall(settings).pipe(map((commonPeriodResponse => [response, commonPeriodResponse])));

					} else {
						return of([response, response]);
					}
				})
			)
			.subscribe(([response, commonPeriodResponse]) => {
				this.processSuccessfulAnalysis(
					get(response, 'portfolioAnalytics', []).filter(port => isNil(port.status)),
					{ config: this.storeConfig.performance }
				);
				this.processSuccessfulAnalysis(
					get(commonPeriodResponse, 'portfolioAnalytics', []).filter(port => isNil(port.status)),
					{ config: this.storeConfig.performanceCommonPeriods }
				);
				this.processFailedAnalysis(response, { config: this.storeConfig.performance });
				this.processFailedAnalysis(commonPeriodResponse, { config: this.storeConfig.performanceCommonPeriods });
			});

	}

	protected isValidAnalysisData(portfolios: InputPortfolio[]): boolean {
		const tolerance = this.configService.get<number>(PeConfigKey.HOLDINGS_ERROR_WEIGHT_TOLERANCE, 0.01) / 100;
		return portfolios.every(port => {
			const total = port.positions.reduce((acc, pos) => acc + pos.weight, 0);
			return Math.abs(1 - total) < tolerance;
		});
	}

	protected dispatchFetchAwaPortfolioAnalysisApiCall(apiSettings: ApiSettings): Observable<Distributionanalyticsv1PortfolioAnalyticsResponse> {
		// Create the observable which handles critical errors
		return this.pasApiService.fetchPortfolioAnalysis(apiSettings.wwePortfolioAnalysisParams).pipe(
			first(),
			catchError((error) => {
				this.storeService.upsertMany(apiSettings.config, []);
				if (apiSettings.config.name === 'performance' && this.shouldGetCommonPeriod) {
					this.storeService.upsertMany(this.storeConfig.performanceCommonPeriods, []);
				}
				Object.assign(error, { 'portfolioUniqueId': apiSettings?.wwePortfolioAnalysisParams?.portfolios[0]?.uniqueId });
				throw new ErrorObject(error, apiSettings.config.name);
			})
		);
	}

	// Update the store with the data if succeeded
	protected processSuccessfulAnalysis(portfoliosData: V1PortfolioAnalytics[], apiSettings: ApiSettings): void {
		let dataToStore = portfoliosData.map(portfolioData => {
			const portfolioAnalytics = portfolioData['totalPortfolioAnalysis'];
			// map portfolio data from response
			let objectToStore = Object.assign(
				{},
				pick(portfolioAnalytics['portfolioAnalysis'], [...PortfolioAnalysisStoreMappingConfig[apiSettings.config.name]]),
				pick(portfolioAnalytics, ['id'], ['analysisDate']),
				pick(portfolioAnalytics, [...PortfolioAnalysisStoreMappingConfig[apiSettings.config.name]]) // in case of ruleEvaluations analytics in portfolio monitoring
			);

			// if activePortfolioAnalysis provided, map benchmark data from response
			if (portfolioAnalytics['activePortfolioAnalysis']) {
				objectToStore = Object.assign(
					objectToStore,
					{ activePortfolioAnalysis: pick(portfolioAnalytics['activePortfolioAnalysis'], [...PortfolioAnalysisStoreMappingConfig[apiSettings.config.name]]) },
					pick(portfolioAnalytics, ['benchmarkId'])
				);
			}

			if (portfolioAnalytics['benchmarkAnalysis']) {
				objectToStore = Object.assign(
					objectToStore,
					{ benchmarkAnalysis: pick(portfolioAnalytics['benchmarkAnalysis'], [...PortfolioAnalysisStoreMappingConfig[apiSettings.config.name]]) }
				);
			}

			// TODO agrigore: check custom analytics mapping, but i dont think it is needed?
			// objectToStore.customAnalytics = fromPairs((portfolioData?.['custom']?.['analytics'] ?? []).map(customColumn => [customColumn['name'], customColumn]));

			return objectToStore;
		});

		// temp fix until suppressZeroContributors works on AWA side
		if (this.shouldIgnoreEmptyPasSleeve && apiSettings.config.name === 'details') {
			dataToStore.forEach(data => {
				data.sleeves.filter(sleeve => sleeve.sleeveId === 'multiAsset' || sleeve.sleeveId === 'alternatives').forEach(sleeve => {
					if (sleeve.sleeveAnalysis?.characteristics?.length) {
						const characteristics = sleeve.sleeveAnalysis?.characteristics;
						characteristics.forEach(analytics => {
							if (analytics.total === 0) {
								analytics.total = undefined;
							}
						});
					}
				});
			});
		}

		if (this.shouldAddBenchPerfAnalysisAtRoot && apiSettings.config.name === 'performance') {
			const benchmarkData = dataToStore.map(analysis => ({ ...analysis.benchmarkAnalysis, id: analysis['benchmarkId']?.split('benchmark-')[1] }));
			this.storeService.selectByIds(this.storeConfig.performance, benchmarkData.map(data => data.id)).pipe(take(1)).subscribe(res => {
				if (isEmpty(res.entities)) {
					dataToStore = [...benchmarkData, ...dataToStore];
				}
				this.updateStoreFromResponse([apiSettings.config], dataToStore);
			});
		} else {
			this.updateStoreFromResponse([apiSettings.config], dataToStore);
		}
	}

	// If any results failed then throw a partial error
	private processFailedAnalysis(response: any, apiSettings: ApiSettings): void { // Distributionanalyticsv1PortfolioAnalyticsResponse
		if (response.errorCodes || response?.portfolioAnalytics?.some(portfolio => portfolio.status)) { // update to be based on status and on unmappedPositions!!!!!!!!!!!!!!!!!!!
			const requestPortfolios = apiSettings.wwePortfolioAnalysisParams.portfolios;
			const failedPortfolios = keys(response.errorCodes)
				.map(portfolioId =>
					get(requestPortfolios.find(portfolio => portfolio.uniqueId === portfolioId), 'description'))
				.filter(portfolioName => Boolean(portfolioName));
			const errorMap = new Map();
			toPairsIn(response.errorCodes).forEach((errorCode: [string, any]) => errorMap.set(errorCode[0], errorCode[1])); //ErrorCode to be updated in pas

			// RemoveStaleAnalysisForFailedPortfolios currently only set to true for PA to handle universe discrepancies between WDS and AWA
			if (this.removeStaleAnalysisForFailedPortfolios) {
				const failedPortfolioIds = Array.from(errorMap.keys());
				this.storeService.removeMany(apiSettings.config, failedPortfolioIds);
			}
			this.storeService.upsertMany(apiSettings.config, []);
			throw new ErrorObject(new WwePortfolioAnalysisPartialError(failedPortfolios, errorMap), apiSettings.config?.name);
		}
	}

	protected dispatchAllDataLoaders(
		configs: Array<ApiStoreConfiguration>,
		generalAnalysisParams: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		configs.forEach(config => {
			this.storeService.fetch(config);
			const apiSettings: ApiSettings = {
				rcs: this.generateRcs([config]),
				portfolioAnalysisParams: null,
				config: config
			};
			this.dispatchDataLoaders(apiSettings, generalAnalysisParams, portfolioAnalysisOptions);
		});
	}

	/**
	 * Dispatches the appropriate service handler to populate the corresponding store.
	 * For the performance calls (gross or net), it checks if performanceAnalysisParams are provided, and if they are,
	 * they will be used to build the performance calls instead of the generalAnalysisParams.
	 *
	 * PerformanceAnalysisParams are provided when user is a partner user.
	 * If a managed model is analysed either individually or as part of a blend, performanceAnalysisParams are built with performance id instead of holdings id.
	 *
	 * @param apiSettings api settings included the rcs to make the call with
	 * @param generalAnalysisParams analysis params used to build all calls*
	 * @param portfolioAnalysisOptions analysis options used to build API calls
	 */
	protected dispatchDataLoaders(
		apiSettings: ApiSettings,
		generalAnalysisParams: AnalysisParams,
		portfolioAnalysisOptions?: PortfolioAnalysisOptions
	): void {
		const apiSettingMethodName = apiSettings.config?.dataLoaderMethodName || apiSettings.config?.name;
		switch (apiSettingMethodName) {
			case DataServiceApiConstants.STORES.MB_DETAILS:
				apiSettings.rcs = DataServiceApiConstants.PORTFOLIO_ANALYSIS_RCS.mbDetails;
				this.loadDefaultStoreData(apiSettings, generalAnalysisParams);
				break;
			default:
				this.loadDefaultStoreData(apiSettings, generalAnalysisParams, portfolioAnalysisOptions);
				break;
		}
	}

	/**
	* Add or Update store with response.
	*
	* If performanceToHoldingIdMap is provided, it checks if the id from the response (which means the one the API call was made with)
	* is a key in performanceToHoldingIdMap (which means the id is performance id) and if it does, it gets the holdings id from the mapping
	* and updates the response and store with it.

	* @param configs store configs to update
	* @param response response used to update the store configs
	* @param responseJson raw response used to handleResponseError
	* @param skipErrorHandler flag to skip error handling, defaulted to false
	* @param performanceToHoldingIdMap mapping of performance id to holdings id
	*/
	private updateStoreFromResponse(
		configs: Array<ApiStoreConfiguration>,
		response: any,
		responseJson?: any,
		skipErrorHandler: boolean = false,
		performanceToHoldingIdMap?: Map<string, string>
	): void {
		if (!isEmpty(response)) {
			configs.forEach(config => {
				if (config.asOfDateType === DataServiceApiConstants.AS_OF_DATE_TYPES.PRE_APPROVED) {
					this.storeService.upsertMany(
						config,
						response.map(res =>
							Object.assign(
								{ id: DataServiceApiConstants.STORES_PREFIXES.REBALANCE_PREFIX + res.portfolioId },
								Object.assign(res, { id: DataServiceApiConstants.STORES_PREFIXES.REBALANCE_PREFIX + res.portfolioId })
							)
						)
					);
				} else if (config.asOfDateType === DataServiceApiConstants.AS_OF_DATE_TYPES.LATEST) {
					this.storeService.upsertMany(
						config,
						response.map(res =>
							Object.assign(
								{ id: DataServiceApiConstants.STORES_PREFIXES.LATEST_PREFIX + res.portfolioId },
								Object.assign(res, { id: DataServiceApiConstants.STORES_PREFIXES.LATEST_PREFIX + res.portfolioId })
							)
						)
					);
				} else {
					this.storeService.upsertMany(
						config,
						response.map(res => {

							const responsePortfolioId = String(res.portfolioId || res.id || res.uniqueId);

							// map the response to the holdings id if responsePortfolioId is performance id
							let portfolioId = responsePortfolioId;
							let responseToUpdate = cloneDeep(res);
							if (!isEmpty(performanceToHoldingIdMap)) {
								portfolioId = performanceToHoldingIdMap.get(responsePortfolioId) || responsePortfolioId;
								responseToUpdate = this.getUpdatedResponse(responsePortfolioId, cloneDeep(res), performanceToHoldingIdMap);
							}

							return Object.assign(
								{
									id: portfolioId,
									name: this.contentService.get(portfolioId, CommonDCR[portfolioId] || portfolioId)
								},
								Object.assign(responseToUpdate, { id: portfolioId })
							);
						})
					);
				}
				// throw error after update store, so components still render when not all data returned
				this.handleResponseError(responseJson, config.name, skipErrorHandler);
			});
		} else {
			configs.forEach(config => {
				// unload store before throwing error
				this.storeService.upsertMany(config, []);
			});
			configs.forEach(config => {
				this.handleResponseError(responseJson, config.name, skipErrorHandler);
			});

		}
	}

	/**
	 * Updates any performance id occurrences with the corresponding holdings id from performanceToHoldingIdMap in the provided response and returns it.
	 * If the response does not have any performance id for models either at the response level or as part of a blend, the provided response is returned.
	 *
	 * @param responsePortfolioId id in response (can either be holdings id or performance id)
	 * @param responseToUpdate
	 * @param performanceToHoldingIdMap mapping of any existent performance id to holding id
	 */
	private getUpdatedResponse(responsePortfolioId: string, responseToUpdate: any, performanceToHoldingIdMap: Map<string, string>): any {
		const debugDataEntries = Object.entries(get(responseToUpdate, 'returns.debugData', {}));
		const parentHoldingsId = performanceToHoldingIdMap.get(responsePortfolioId);

		const isModelAnalysed = !isNil(parentHoldingsId);

		let returnsToUpdate = cloneDeep(get(responseToUpdate, 'returns', {}));

		// update debugData if provided
		if (!isEmpty(debugDataEntries)) {
			const debugData = this.updateDebugData(debugDataEntries, performanceToHoldingIdMap);
			returnsToUpdate = { ...returnsToUpdate, debugData };
		}

		// update response ids if model is analysed directly (not as part of blend)
		if (isModelAnalysed) {
			const uniqueId = get(returnsToUpdate, 'uniqueId');
			const updatedId = replace(uniqueId, responsePortfolioId, parentHoldingsId);
			returnsToUpdate = { ...returnsToUpdate, uniqueId: updatedId };

			if (responseToUpdate.portfolioId) { // update portfolioId if provided (not provided for portfolios)
				responseToUpdate = { ...responseToUpdate, portfolioId: +parentHoldingsId };
			}

			if (responseToUpdate.identifier) { // update portfolioId if provided (not provided for portfolios)
				responseToUpdate = { ...responseToUpdate, identifier: parentHoldingsId };
			}
		}

		return Object.assign({}, responseToUpdate, { returns: { ...returnsToUpdate } });
	}

	/**
	 * For the provided debugDataEntries, checks if any of the debug data ids are performance ids in performanceToHoldingIdMap and:
	 * - if debug id is performance id, it replaces all occurrences of it with holdings id
	 * - if debug id is not performance id, does not do any change
	 *
	 * @param debugDataEntries debug data entries to update (['id', {startDate: 20200102, returnsId: modelId ...}])
	 * @param performanceToHoldingIdMap
	 */
	private updateDebugData(debugDataEntries: any, performanceToHoldingIdMap: Map<string, string>): any {
		const performanceIdsToMap = Array.from(performanceToHoldingIdMap.keys());

		const updatedDebugDataEntries = debugDataEntries.map(([portfolioKey, portfolioValue]: any[]) => {

			const performanceIdToMap = performanceIdsToMap.find(performanceId => endsWith(portfolioKey, ':' + performanceId));
			if (!isNil(performanceIdToMap)) {
				const holdingsId = performanceToHoldingIdMap.get(performanceIdToMap);
				const updatedId = replace(portfolioKey, performanceIdToMap, holdingsId); // can either be PORTFOLIO_ID:modelId if models is analysed or MODEL_ID:modelId if model is analysed as part of blend

				return [updatedId, { ...portfolioValue, returnsId: holdingsId }];
			} else {
				return [portfolioKey, portfolioValue];
			}
		});

		// transform the entries to object
		return updatedDebugDataEntries.reduce((acc, [key, value]) => {
			acc[key] = value;
			return acc;
		}, {});
	}

	/**
	 * throw error to be handled by application handler while responseJson:
	 * success = false OR
	 * success = true, allDataReturned = false
	 */
	private handleResponseError(responseJson: any, configName: string, skipErrorHandler: boolean): void {
		// TODO wkennerl: clean up references to PORTFOLIO_ANALYSIS_HOLDING_NOT_FOUND
		const isHoldingMissing = responseJson?.errorCodes instanceof Array && responseJson.errorCodes.includes(ERROR_CODES.PORTFOLIO_ANALYSIS_HOLDING_NOT_FOUND);

		if (!skipErrorHandler && responseJson && (!(responseJson.success && responseJson.allDataReturned) || isHoldingMissing)) {
			throw new ErrorObject(responseJson, configName);
		}
	}

	/**
	 * Return combined rcs extract from config
	 */
	private generateRcs(configs: Array<ApiStoreConfiguration>): any {
		return configs.map(config => DataServiceApiConstants.PORTFOLIO_ANALYSIS_RCS[config.name]).join(',');
	}

	/**
	 * Handle the response from /wds/api/config-file/bfa:model-portfolios
	 * Comment out error handling for now, need to check how to isolate the services
	 */
	private getModelPortfoliosListResponse(responseJson: any, shouldFilterResearchBlkModel: boolean = false): any {
		if (!responseJson) {
			throw new Error(this.contentService.get('PORTFOLIO_ANALYSER_CONFIG_ERROR', ErrorDCR.PORTFOLIO_ANALYSER_CONFIG_ERROR));
		} else {
			const response: Map<string, any> = new Map(); // using a map to preserve the order
			const defaultModels = responseJson.defaultModels?.modelFamilies || [];
			const defaultPortfolios = compact(flatten(defaultModels.map(family => family.models))); // listing all ADAM portfolios to update managed models name source

			const managedModelsResponse = responseJson.managedModels || {};
			if (!isEmpty(managedModelsResponse)) {
				get(managedModelsResponse, 'portfolioFamilies', []).forEach(family => {
					const familyObject = {
						showBenchmark: family.showBenchmark,
						isDefaultFamilyForVehiclePref: family.isDefault,
						vehicle1: family.vehicle1,
						vehicle2: family.vehicle2,
						familyCode: family.portfolioFamilySchema.code,
						familyName: family.portfolioFamilySchema.name,
						rebalDocId: family.portfolioFamilySchema.rebalDocId,
						docIds: family.portfolioFamilySchema.docIds,
						subType: 'BLK',
						enableAdamModelNames: this.enableAdamModelNames
					};

					get(family, 'portfolioFamilySchema.portfolios', []).forEach(portfolio => {
						const portfolioObject = {
							...familyObject,
							...portfolio,
							name: this.updateToAdamModelName(String(portfolio.id), defaultPortfolios) || portfolio.name,
							dasPortfolioId: portfolio.id,
							aladdinPortfolioCode: this.mapManagedModelDasIdToAPCFromDefaultModels(String(portfolio.id), defaultModels) || portfolio.aladdinPortfolioCode,
							groups: [{
								id: SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP,
								name: SharedApplicationConstants.MODEL_FAMILY.BLACKROCK_FAMILY_GROUP_TITLE
							}] // Adding default blk group to the managed models as filtering out models without groups array instead of mapping them to blk model
						};
						response.set(String(portfolioObject.id), portfolioObject);
					});
				});
			}
			const responseKey = this.enablePasThirdPartyApi ? 'thirdParty.modelFamilies' : 'thirdParty.response';
			const modelsKey = this.enablePasThirdPartyApi ? 'models' : 'portfolios';
			const thirdPartyResponse = get(responseJson, responseKey, []);
			if (!isEmpty(thirdPartyResponse)) {
				thirdPartyResponse.forEach(family => {
					const familyObject = {
						familyCode: family.id,
						familyName: this.enablePasThirdPartyApi ? family.name : family.description,
						isDefaultFamilyForVehiclePref: true,
						subType: 'TPM'
					};

					get(family, modelsKey, []).forEach(portfolio => {
						const portfolioObject = {
							...familyObject,
							id: portfolio.id,
							name: this.enablePasThirdPartyApi ? portfolio.name : portfolio.description,
							managementType: this.enablePasThirdPartyApi ?
								portfolio?.managementType : portfolio?.allData?.managementType
						};
						response.set(String(portfolioObject.id), portfolioObject);
					});
				});
			}



			if (!isEmpty(defaultModels)) {
				defaultModels
					.filter(family => !shouldFilterResearchBlkModel || family?.subType !== 'BLK')
					.forEach(family => {
						const familyObject = {
							familyCode: family.id,
							familyName: family.description,
							isDefaultFamilyForVehiclePref: true,
							subType: family.subType,
							groups: family.groups,
							enableAdamModelNames: this.enableAdamModelNames,
							enableCmsRenaming: this.enableCmsRenaming,
							displayOrder: family?.displayOrder,
							modelExternalFamilyName: family?.modelExternalName
						};

						get(family, 'models', []).forEach(portfolio => {
							const managementType = portfolio?.managementType || family?.managementType;
							const id = this.flipModelsConfigStoreIdentifierToDasPortfolioId ? portfolio?.portfolioId : portfolio.id;
							const portfolioObject = {
								...familyObject,
								id,
								name: portfolio.description,
								managementType: managementType,
								groups: portfolio.groups || family.groups,
								dasModelOrder: portfolio?.dasModelOrder,
								shortName: portfolio?.shortName,
								modelExternalName: portfolio?.modelExternalName,
								dasPortfolioId: portfolio?.portfolioId,
								aladdinPortfolioCode: portfolio?.aladdinPortfolioCd
							};

							if (portfolio?.performanceId && managementType?.toLowerCase() === 'managed') {
								portfolioObject['performanceId'] = portfolio?.performanceId;
								if (!isEmpty(family?.documentIds)) {
									portfolioObject['docIds'] = family?.documentIds?.map(document => {
										const documentSplitArr = split(document, ':', 2);
										return {
											materialType: head(documentSplitArr),
											docId: last(documentSplitArr)
										};
									});
								}
							}

							const portfolioId = String(portfolioObject.id);
							if (response.has(portfolioId)) {
								Object.assign(response.get(portfolioId), portfolioObject);
							} else {
								response.set(portfolioId, portfolioObject);
							}
						});
					});
			}

			return Array.from(response.values());
		}
	}

	/**
	 * Update the managed model name source to ADAM models
	 */
	private updateToAdamModelName(id: string, portfolios: Array<{ [id: string]: any }>): string {
		const adamPortfolio = portfolios.find(portfolio => (portfolio?.portfolioId || portfolio.id) === id);
		return adamPortfolio?.description;
	}

	private mapManagedModelDasIdToAPCFromDefaultModels(dasId: string, defaultModels): string {
		let apc: string;

		for (const family of defaultModels) {
			const foundId = family.models?.find(model => dasId === model.portfolioId)?.aladdinPortfolioCd;
			if (!isNil(foundId)) {
				apc = foundId;
				break;
			}
		}
		return apc;
	}
}
